import { Injectable, inject } from '@angular/core';
import type { HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, tap } from 'rxjs';
import { Notification, NotificationLiveTime, NotificationService } from '@shared/notifications';
import type { Settings } from '@shared/settings';
import { SettingsResource } from '@shared/settings';
import type { RequestEmailChangePayload } from './email-api.service';
import { EmailApiService } from './email-api.service';

@Injectable({
  providedIn: 'root',
})
export class EmailService {
  private readonly settings = inject(SettingsResource);
  private readonly resource = inject(EmailApiService);
  private readonly notifications = inject(NotificationService);

  public requestEmailChange(payload: RequestEmailChangePayload) {
    return this.resource.requestEmailChange(payload).pipe(
      tap(() => {
        this.notifications.show(
          Notification.toast().asWarning({
            messageKey: 'CHANGE_EMAIL_SUCCESS',
            messageParams: { email: payload.newEmailAddress },
            liveTime: NotificationLiveTime.UNTIL_TAPPED,
          }),
        );
      }),
      catchError((error) => {
        this.onEmailChangeRequestFailed(error);
        return EMPTY;
      }),
    );
  }

  public updateAlertingEmailIfRequested(userId: number, emailForAlerts: string, settings: Settings) {
    const { phoneNumber } = settings.alertingEngineSettingsModel || {};

    return this.settings.update(userId, {
      trackMe: settings.trackMe,
      emailForAlerts,
      phoneNumberForAlerts: phoneNumber,
    });
  }

  private onEmailChangeRequestFailed({ error }: HttpErrorResponse) {
    // default error message
    let messageKey = 'CHANGE_EMAIL_ERROR';

    const isWrongPassword = (errors: { [key: string]: { code; params }[] }) =>
      !!errors.currentPassword?.length && errors.currentPassword[0].code === 'WRONG_PASSWORD';

    if (!!error?.errors && isWrongPassword(error.errors)) {
      messageKey = 'CHANGE_EMAIL_ERROR_CREDENTIALS';
    }

    this.notifications.show(Notification.toast().asError({ messageKey }));
  }
}
